import { type PreprocessedDataPoint } from '../../processors/lapComparison/types'
import { type TelemetryMetric } from '../metrics/TelemetryMetricTypes'

/**
 * Extract metric value from a preprocessed data point.
 *
 * NOTE: The `brake` case intentionally returns the full signed acceleration
 * (a combined brake/throttle gradient). This differs from the lapComparison
 * gradient extractor — keep them separate; they back different visuals.
 */
export function extractMetricFromPreprocessed(
  point: PreprocessedDataPoint,
  metric: TelemetryMetric
): number | undefined {
  switch (metric) {
    case 'speed':
      return point.speed
    case 'timeDrift':
      return point.timeDrift
    case 'coasting':
      return point.coasting
    case 'leanAngle':
      return point.leanAngle !== undefined
        ? Math.abs(point.leanAngle)
        : undefined
    case 'lateralG':
      return point.lateralG !== undefined ? Math.abs(point.lateralG) : undefined
    case 'acceleration':
      return point.acceleration
    case 'brake':
      // For brake metric, use full acceleration range (negative=brake, positive=throttle)
      // This creates a combined brake/throttle gradient
      return point.acceleration
    default:
      return undefined
  }
}

/**
 * Normalize time drift with stronger contrast than the original but smooth transitions.
 * Uses a power curve (t^0.65) to push colors toward vivid red/green faster than linear
 * while avoiding the abrupt jumps that sqrt caused.
 * Neutral zone (±0.5 ms/m) stays in the yellow range; full saturation at ~6.5 ms/m.
 */
export function normalizeTimeDrift(rate: number): number {
  // Neutral zone: near-zero rates stay in yellow/gold range
  if (rate >= -0.5 && rate <= 0.5) {
    return 0.5 + (rate / 0.5) * 0.05
  }
  // Losing time (positive rate) - power curve for faster color saturation
  if (rate > 0.5) {
    const t = Math.min(1, (rate - 0.5) / 6)
    return 0.55 + Math.pow(t, 0.65) * 0.45
  }
  // Gaining time (negative rate) - power curve for faster color saturation
  const t = Math.min(1, (Math.abs(rate) - 0.5) / 6)
  return 0.45 - Math.pow(t, 0.65) * 0.45
}
